import React from 'react';
import Marca from '../Marca';
import { NavLink, useLocation } from 'react-router-dom';
import { authService } from '../../services/auth';
import {
  Car, Users, UserCheck, Shield, Fuel, Wrench, DollarSign, MapPin, BarChart3, Settings, Home, FileText, Bell, ScrollText, ShieldCheck,
  ChevronDown, SlidersHorizontal, Tag
} from 'lucide-react';
import { usePermissoes } from '../../hooks/usePermissoes';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();

  const { pode } = usePermissoes();

  const menuItems = [
    {
      title: 'Dashboard',
      icon: Home,
      path: '/dashboard',
      description: 'Visão geral do sistema'
    },
    {
      title: 'Veículos',
      icon: Car,
      path: '/veiculos',
      description: 'Gestão da frota'
    },
    {
      title: 'Motoristas',
      icon: Users,
      path: '/motoristas',
      description: 'Cadastro de motoristas'
    },
    {
      title: 'Abastecimentos',
      icon: Fuel,
      path: '/abastecimentos',
      description: 'Controle de combustível'
    },
    {
      title: 'Manutenções',
      icon: Wrench,
      path: '/manutencoes',
      description: 'Serviços e reparos'
    },
    {
      title: 'Financeiro',
      icon: DollarSign,
      path: '/despesas',
      description: 'Gestão de despesas'
    },
    {
      title: 'Categorias',
      icon: Tag,
      path: '/despesas/categorias',
      description: 'Categorias de despesa'
    },
    {
      title: 'Viagens',
      icon: MapPin,
      path: '/viagens',
      description: 'Controle de viagens'
    },
    {
      title: 'Relatórios',
      icon: BarChart3,
      path: '/relatorios',
      description: 'Análises e gráficos'
    },
  ];


  // Um modulo que o perfil nao pode nem consultar sai do menu: deixar o item la
  // so leva a uma tela de erro.
  const MODULO_POR_ROTA: Record<string, string> = {
    '/veiculos': 'veiculos',
    '/motoristas': 'motoristas',
    '/abastecimentos': 'abastecimentos',
    '/manutencoes': 'manutencoes',
    '/despesas': 'despesas',
    '/despesas/categorias': 'despesas',
    '/viagens': 'viagens',
    '/relatorios': 'relatorios',
  };

  const menuVisivel = menuItems.filter((item) => {
    const modulo = MODULO_POR_ROTA[item.path];
    return !modulo || pode(modulo, 'consultar');
  });

  const bottomItems = [
    // Estes quatro seguem a permissao configurada pela empresa, e nao mais o
    // papel: se a empresa liberar aprovacoes para o gestor, o item aparece para
    // ele. O servidor confere de novo em cada rota.
    ...(pode('aprovacoes', 'consultar') ? [
      {
        title: 'Aprovações',
        icon: UserCheck,
        path: '/aprovacoes',
        description: 'Aprovar motoristas'
      },
    ] : []),
    ...(pode('usuarios', 'consultar') ? [
      {
        title: 'Usuários',
        icon: Users,
        path: '/usuarios',
        description: 'Gerenciar usuários'
      },
    ] : []),
    ...(pode('usuarios', 'editar') ? [
      {
        title: 'Permissões',
        icon: ShieldCheck,
        path: '/permissoes',
        description: 'O que cada perfil pode fazer'
      },
    ] : []),
    ...(pode('notificacoes', 'consultar') ? [
      {
        title: 'Notificações',
        icon: Bell,
        path: '/notificacoes',
        description: 'Quem recebe cada aviso'
      },
    ] : []),
    ...(pode('auditoria', 'consultar') ? [
      {
        title: 'Auditoria',
        icon: ScrollText,
        path: '/auditoria',
        description: 'Histórico de ações'
      },
    ] : []),
    {
      title: 'Alertas',
      icon: Bell,
      path: '/alertas',
      description: 'Notificações'
    },
    {
      title: 'Documentos',
      icon: FileText,
      path: '/documentos',
      description: 'Arquivos importantes'
    },
    {
      title: 'Configurações',
      icon: Settings,
      path: '/configuracoes',
      description: 'Configurações do sistema'
    },
  ];

  /**
   * Destaca o item mais específico, e não todos os que casam.
   *
   * `/despesas/categorias` começa com `/despesas`: sem isto, os dois itens
   * acendiam juntos e a tela deixava de dizer onde a pessoa está.
   */
  const caminhoAtivo = [...menuItems, ...bottomItems]
    .map((item) => item.path)
    .filter((path) => location.pathname === path || location.pathname.startsWith(path + '/'))
    .sort((a, b) => b.length - a.length)[0];

  const isActive = (path: string) => path === caminhoAtivo;

  // Os itens de administracao viram um grupo recolhivel. Eram ate oito links
  // empilhados abaixo do menu principal, e numa tela de notebook empurravam
  // Relatorios e Viagens para fora da area visivel: o menu que o cliente usa
  // todo dia ficava competindo por espaco com telas de configuracao que ele
  // abre uma vez por mes.
  const CHAVE_ADMIN_ABERTO = 'frovox:menu-admin-aberto';

  const algumAdminAtivo = bottomItems.some((item) => isActive(item.path));

  const [adminAberto, setAdminAberto] = React.useState<boolean>(() => {
    try {
      const salvo = window.localStorage.getItem(CHAVE_ADMIN_ABERTO);
      if (salvo !== null) {
        return salvo === '1';
      }
    } catch {
      // Navegador em aba anonima ou com armazenamento bloqueado lanca ao ler.
      // Sem preferencia salva, comeca recolhido.
    }
    return false;
  });

  // Entrar numa tela do grupo abre o grupo: caso contrario o usuario navega
  // para Permissoes e o menu nao mostra onde ele esta.
  React.useEffect(() => {
    if (algumAdminAtivo) {
      setAdminAberto(true);
    }
  }, [algumAdminAtivo]);

  const alternarAdmin = () => {
    setAdminAberto((aberto) => {
      const proximo = !aberto;
      try {
        window.localStorage.setItem(CHAVE_ADMIN_ABERTO, proximo ? '1' : '0');
      } catch {
        // Preferencia nao persistida: o grupo ainda abre e fecha nesta sessao.
      }
      return proximo;
    });
  };

  return (
    <>
      {/* Overlay para mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      {/*
        flex-col e obrigatorio aqui: o <nav> tem flex-1 e overflow-y-auto, mas sem
        um pai em coluna o flex-1 nao vale nada. O menu crescia alem da altura da
        tela e simplesmente nao rolava, entao num notebook de 720px os ultimos
        itens (Permissoes, Auditoria, Alertas, Documentos, Configuracoes) ficavam
        fora da area visivel e inalcancaveis.
      */}
      <div className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out
        flex flex-col
        lg:translate-x-0 lg:static lg:h-screen
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200 shrink-0">
          {/* Simbolo, e nao a marca completa: com 36px o texto dentro do
              circulo seria ilegivel. O nome vem ao lado, em texto de verdade. */}
          <Marca versao="simbolo" tamanho={36} mostrarNome />
          
          {/* Botao fechar no mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md hover:bg-secondary-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Menu Principal */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {menuVisivel.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => window.innerWidth < 1024 && onClose()}
                className={`
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${active 
                    ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600' 
                    : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                  }
                `}
              >
                <Icon className={`
                  mr-3 h-5 w-5 transition-colors duration-200
                  ${active ? 'text-primary-600' : 'text-secondary-400 group-hover:text-secondary-600'}
                `} />
                <div className="flex-1">
                  <div className="font-medium">{item.title}</div>
                  <div className={`
                    text-xs mt-0.5
                    ${active ? 'text-primary-600' : 'text-secondary-400'}
                  `}>
                    {item.description}
                  </div>
                </div>
              </NavLink>
            );
          })}
        </nav>

        {/* Administracao: grupo recolhivel */}
        <div className="border-t border-secondary-200 px-4 py-4">
          <button
            type="button"
            onClick={alternarAdmin}
            aria-expanded={adminAberto}
            aria-controls="menu-administracao"
            className="w-full group flex items-center px-3 py-2 text-sm font-medium rounded-lg text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 transition-colors duration-200"
          >
            <SlidersHorizontal className="mr-3 h-4 w-4 text-secondary-400 group-hover:text-secondary-600" />
            <span className="flex-1 text-left">Administração</span>
            {/* Recolhido, um ponto avisa que a tela aberta esta aqui dentro. Sem
                isso o usuario perde a referencia de onde esta navegando. */}
            {!adminAberto && algumAdminAtivo && (
              <span className="mr-2 h-1.5 w-1.5 rounded-full bg-primary-600" aria-hidden="true" />
            )}
            <ChevronDown
              aria-hidden="true"
              className={`h-4 w-4 text-secondary-400 transition-transform duration-200 ${adminAberto ? 'rotate-180' : ''}`}
            />
          </button>

          <div id="menu-administracao" hidden={!adminAberto} className="mt-1 space-y-1">
          {bottomItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => window.innerWidth < 1024 && onClose()}
                className={`
                  group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200
                  ${active 
                    ? 'bg-primary-50 text-primary-700' 
                    : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                  }
                `}
              >
                <Icon className={`
                  mr-3 h-4 w-4 transition-colors duration-200
                  ${active ? 'text-primary-600' : 'text-secondary-400 group-hover:text-secondary-600'}
                `} />
                {item.title}
              </NavLink>
            );
          })}
          </div>
        </div>

        {/* Super Admin - so aparece para superadmin */}
        {authService.getUser()?.is_super_admin && (
          <div className="border-t border-red-200 px-4 py-3">
            <NavLink
              to="/super-admin"
              onClick={() => window.innerWidth < 1024 && onClose()}
              className={`
                group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                ${isActive('/super-admin')
                  ? 'bg-red-50 text-red-700 border-r-2 border-red-600'
                  : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                }
              `}
            >
              <Shield className={`mr-3 h-5 w-5 ${isActive('/super-admin') ? 'text-red-600' : 'text-red-400 group-hover:text-red-600'}`} />
              <div className="flex-1">
                <div className="font-medium">Super Admin</div>
                <div className="text-xs text-red-400">Gestão de empresas</div>
              </div>
            </NavLink>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-secondary-200 p-4 shrink-0">
          <div className="text-xs text-secondary-500 text-center">
            {/* Ano calculado na hora: fixo no codigo, ele envelhece em silencio e
                o rodape passa a anunciar um ano errado a cada virada. */}
            © {new Date().getFullYear()} FROVOX
            <br />
            Versão 1.0.0
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
